import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, MailX } from "lucide-react";
import { useEffect } from "react";
import { ErrorPanel } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { AccountFrame, InvitationList } from "@/features/accounts/shared";
import { q } from "@/lib/api";

/** Invitation link (emailed, or copied from Settings → Members): accept as the invited email. */
export const Route = createFileRoute("/join/$invitationId")({
  component: JoinPage,
});

function JoinPage() {
  const { invitationId } = Route.useParams();
  const me = useQuery(q.me());
  const navigate = useNavigate();
  const invitation = me.data?.invitations.find((i) => i.id === invitationId);

  useEffect(() => {
    document.title = invitation ? `Join ${invitation.orgName} · traces.insure` : "Join an organization · traces.insure";
  }, [invitation]);

  return (
    <AccountFrame
      aside={
        me.data ? (
          <span className="truncate text-xs text-fg-3">
            Signed in as <span className="text-fg-2">{me.data.email}</span>
          </span>
        ) : null
      }
    >
      {me.error ? (
        <ErrorPanel error={me.error} reset={() => void me.refetch()} />
      ) : !me.data ? (
        <Skeleton className="h-40 w-full" />
      ) : invitation ? (
        <section aria-labelledby="join-title" className="flex flex-col gap-3">
          <div>
            <h1 id="join-title" className="text-lg font-semibold tracking-tight">
              Join {invitation.orgName}
            </h1>
            <p className="text-xs text-fg-3">{invitation.invitedBy} invited you. Accepting adds you to the workspace with the role below.</p>
          </div>
          <InvitationList invitations={[invitation]} highlight={invitation.id} onAccepted={() => void navigate({ to: "/" })} />
        </section>
      ) : (
        <section aria-labelledby="join-missing" className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
          <MailX className="size-6 text-fg-3" aria-hidden />
          <h1 id="join-missing" className="text-sm font-medium">
            This invitation isn't available
          </h1>
          <p className="max-w-md text-xs text-fg-3">
            It may have expired, been revoked or already been used, or it was sent to a different email address than <span className="text-fg-2">{me.data.email}</span>. Ask the person who invited you for a new link, and sign in with the invited
            address.
          </p>
          <Button variant="outline" size="sm" asChild className="mt-2">
            <Link to={me.data.orgs.length ? "/" : "/signup"}>
              {me.data.orgs.length ? "Go to your dashboard" : "Create an organization instead"} <ArrowRight />
            </Link>
          </Button>
          {me.data.invitations.length ? <p className="text-[11px] text-fg-3">You have {me.data.invitations.length} other pending invitation{me.data.invitations.length === 1 ? "" : "s"}; see them on the signup page.</p> : null}
        </section>
      )}
    </AccountFrame>
  );
}
